import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
} from '@mui/material';
import { Sparkles, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { tracker } from '../analytics';

const getDaysRemaining = (trialEnd) => {
  if (!trialEnd) return null;
  const diff = new Date(trialEnd) - new Date();
  if (isNaN(diff)) return null;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

/**
 * Sticky trial banner — full-width header shown on top of a page.
 * Renders only when the user's subscription is in trial.
 */
export const TrialBanner = ({ sticky = true, source = 'unknown' }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const subscription = useSelector((state) => state.subscription?.data);

  const isDark = theme.palette.mode === 'dark';
  const goldColor = theme.palette.primary?.main || '#c9a96e';

  const daysRemaining = useMemo(
    () => getDaysRemaining(subscription?.trial_end),
    [subscription?.trial_end]
  );

  if (subscription?.status !== 'trial') return null;

  const handleSubscribe = () => {
    ReactGA.event({
      category: 'Subscription',
      action: 'Trial Banner Click',
      label: source,
    });
    tracker.event({
      category: 'Subscription',
      action: 'Trial Banner Click',
      label: source,
    });
    navigate('/subscribe');
  };

  const title = (() => {
    if (daysRemaining == null) {
      return t('trial_banner_generic_title', { defaultValue: 'You are on a free trial' });
    }
    if (daysRemaining === 1) {
      return t('trial_banner_days_remaining_one', {
        count: daysRemaining,
        defaultValue: 'Your free trial ends in {{count}} day',
      });
    }
    return t('trial_banner_days_remaining_other', {
      count: daysRemaining,
      defaultValue: 'Your free trial ends in {{count}} days',
    });
  })();

  const subtitle = t('trial_banner_subtitle', {
    defaultValue: 'Subscribe now to keep access after your trial ends.',
  });

  return (
    <Box
      sx={{
        position: sticky ? 'sticky' : 'relative',
        top: 0,
        zIndex: 10,
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(goldColor, 0.35)}`,
        background: `linear-gradient(135deg, ${alpha(goldColor, isDark ? 0.12 : 0.1)} 0%, ${alpha(goldColor, isDark ? 0.05 : 0.04)} 100%)`,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        p: { xs: 1.75, sm: 2 },
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(goldColor, isDark ? 0.18 : 0.14),
            color: goldColor,
            flexShrink: 0,
          }}
        >
          <Sparkles size={18} strokeWidth={2} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: '12.5px',
              color: theme.palette.text.secondary,
              mt: 0.25,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>

      <Button
        variant="contained"
        disableElevation
        onClick={handleSubscribe}
        sx={{
          bgcolor: goldColor,
          color: '#1a1714',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '13px',
          px: 2.25,
          py: 0.85,
          borderRadius: '8px',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          '&:hover': {
            bgcolor: theme.palette.primary.dark || goldColor,
          },
        }}
      >
        {t('subscribe_now', { defaultValue: 'Subscribe Now' })}
      </Button>
    </Box>
  );
};

/**
 * Compact trial pill for the navbar.
 * - Expanded state: full pill with days remaining and CTA.
 * - Collapsed state: small icon badge with tooltip.
 */
export const NavbarTrialPill = ({ open = true }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const subscription = useSelector((state) => state.subscription?.data);

  const isDark = theme.palette.mode === 'dark';
  const goldColor = theme.palette.primary?.main || '#c9a96e';

  const daysRemaining = useMemo(
    () => getDaysRemaining(subscription?.trial_end),
    [subscription?.trial_end]
  );

  if (subscription?.status !== 'trial') return null;

  const handleClick = () => {
    ReactGA.event({
      category: 'Subscription',
      action: 'Trial Banner Click',
      label: 'Navbar',
    });
    tracker.event({
      category: 'Subscription',
      action: 'Trial Banner Click',
      label: 'Navbar',
    });
    navigate('/subscribe');
  };

  const label = (() => {
    if (daysRemaining == null) {
      return t('trial_nav_generic', { defaultValue: 'Free trial' });
    }
    if (daysRemaining === 1) {
      return t('trial_nav_days_left_one', {
        count: daysRemaining,
        defaultValue: '{{count}} day left',
      });
    }
    return t('trial_nav_days_left_other', {
      count: daysRemaining,
      defaultValue: '{{count}} days left',
    });
  })();

  if (!open) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
        <Tooltip title={`${label} — ${t('subscribe_now', { defaultValue: 'Subscribe Now' })}`} placement="right" arrow>
          <Box
            onClick={handleClick}
            role="button"
            sx={{
              width: 42,
              height: 42,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(goldColor, isDark ? 0.16 : 0.12),
              color: goldColor,
              cursor: 'pointer',
              border: `1px solid ${alpha(goldColor, 0.35)}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(goldColor, isDark ? 0.24 : 0.18),
              },
            }}
          >
            <Clock size={18} strokeWidth={2} />
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1.5, mb: 1 }}>
      <Box
        onClick={handleClick}
        role="button"
        sx={{
          cursor: 'pointer',
          p: 1.25,
          borderRadius: '10px',
          border: `1px solid ${alpha(goldColor, 0.35)}`,
          bgcolor: alpha(goldColor, isDark ? 0.1 : 0.08),
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: alpha(goldColor, isDark ? 0.16 : 0.12),
            borderColor: alpha(goldColor, 0.55),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Clock size={14} strokeWidth={2} color={goldColor} />
          <Typography
            sx={{
              fontSize: '11.5px',
              fontWeight: 600,
              color: goldColor,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Button
          fullWidth
          variant="contained"
          disableElevation
          size="small"
          sx={{
            bgcolor: goldColor,
            color: '#1a1714',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '12px',
            py: 0.6,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: theme.palette.primary.dark || goldColor,
              boxShadow: 'none',
            },
          }}
        >
          {t('subscribe_now', { defaultValue: 'Subscribe Now' })}
        </Button>
      </Box>
    </Box>
  );
};

export default TrialBanner;
